import { createBigDecimal, plus, times } from '@0xtorch/big-decimal'
import { type CryptoCurrency, type FiatCurrency, rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance } from '../../types'
import { createSha256Hash } from '../../utils/createSha256Hash'
import { getAsset } from '../../utils/getAsset'
import { binanceApiEndpoint, binanceSymbolCryptoIdMap } from '../constants'
import { marginAssetSchema } from './schema'

const responseSchema = z.object({
  assets: z.array(
    z.object({
      baseAsset: marginAssetSchema,
      quoteAsset: marginAssetSchema,
      symbol: z.string(),
    }),
  ),
})

const urlPath = '/sapi/v1/margin/isolated/account'

type FetchIsolatedMarginAccountAssetsParameters = {
  apiKey: string
  apiSecret: string
  proxy?: string
  proxyHeaders?: Record<string, string>
  cryptoCurrencies: readonly CryptoCurrency[]
  fiatCurrencies: readonly FiatCurrency[]
}

export const fetchIsolatedMarginAccountAssets = async ({
  apiKey,
  apiSecret,
  proxy,
  proxyHeaders,
  cryptoCurrencies,
  fiatCurrencies,
}: FetchIsolatedMarginAccountAssetsParameters): Promise<
  readonly AssetBalance[]
> => {
  const timestamp = Date.now()

  const url = new URL(urlPath, binanceApiEndpoint)
  url.searchParams.append('timestamp', timestamp.toString())
  url.searchParams.append(
    'signature',
    await createSha256Hash(url.searchParams.toString(), apiSecret),
  )

  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'GET',
        headers: {
          'X-MBX-APIKEY': apiKey,
          ...proxyHeaders,
        },
      },
      schema: responseSchema,
    },
  )

  const assetBalances: AssetBalance[] = []
  for (const assetData of response.assets) {
    // base asset
    const baseAsset = getAsset({
      symbol: assetData.baseAsset.asset,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: binanceSymbolCryptoIdMap,
    })
    if (baseAsset !== undefined) {
      const cryptoId =
        baseAsset.type === 'CryptoCurrency' ? baseAsset.id : undefined
      const fiatId =
        baseAsset.type === 'FiatCurrency' ? baseAsset.id : undefined
      const balance = plus(assetData.baseAsset.free, assetData.baseAsset.locked)
      if (balance.value > 0n) {
        assetBalances.push({
          key: `binance_isolated-margin-balance_${assetData.symbol}`,
          cryptoId,
          fiatId,
          amount: balance,
          timestamp,
        })
      }
      const borrow = times(
        plus(assetData.baseAsset.borrowed, assetData.baseAsset.interest),
        createBigDecimal(-1n),
      )
      if (borrow.value !== 0n) {
        assetBalances.push({
          key: `binance_isolated-margin-borrow_${assetData.symbol}`,
          cryptoId,
          fiatId,
          amount: borrow,
          timestamp,
        })
      }
    }

    // quote asset
    const quoteAsset = getAsset({
      symbol: assetData.quoteAsset.asset,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: binanceSymbolCryptoIdMap,
    })
    if (quoteAsset !== undefined) {
      const cryptoId =
        quoteAsset.type === 'CryptoCurrency' ? quoteAsset.id : undefined
      const fiatId =
        quoteAsset.type === 'FiatCurrency' ? quoteAsset.id : undefined
      const balance = plus(
        assetData.quoteAsset.free,
        assetData.quoteAsset.locked,
      )
      if (balance.value > 0n) {
        assetBalances.push({
          key: `binance_isolated-margin-balance_${assetData.symbol}`,
          cryptoId,
          fiatId,
          amount: balance,
          timestamp,
        })
      }
      const borrow = times(
        plus(assetData.quoteAsset.borrowed, assetData.quoteAsset.interest),
        createBigDecimal(-1n),
      )
      if (borrow.value !== 0n) {
        assetBalances.push({
          key: `binance_isolated-margin-borrow_${assetData.symbol}`,
          cryptoId,
          fiatId,
          amount: borrow,
          timestamp,
        })
      }
    }
  }

  return assetBalances
}
